import { Helmet } from 'react-helmet-async';
import PageLayout from '../../components/PageLayout';
import { MapPin, CheckCircle2, Building2 } from 'lucide-react';

const LuxuryVillaKannur = () => {
  return (
    <>
      <Helmet>
        <title>Luxury Villa Construction Case Study in Kannur | Master Home</title>
        <meta name="description" content="Explore our luxury villa construction project in Kannur. See how Master Home delivered a premium custom home despite timeline and land challenges." />
        <link rel="canonical" href="https://masterhome.co.in/projects/luxury-villa-kannur" />
      </Helmet>

      <PageLayout
        title="Luxury Villa Construction in Kannur"
        subtitle="A stunning example of modern Kerala architecture meeting contemporary luxury."
      >
        <div className="space-y-16">
          {/* Hero Gallery */}
          <div className="rounded-2xl overflow-hidden shadow-2xl relative">
            <img
              src="/custom-home-construction-kannur.jpg"
              alt="Luxury villa exterior construction in Kannur"
              className="w-full h-auto aspect-[21/9] object-cover"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-8">
              <h2 className="text-3xl font-bold text-white">Project Value: ₹1.2 Cr+</h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="md:col-span-2 space-y-12">
              {/* Overview */}
              <section className="prose max-w-none text-gray-700">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 border-b pb-4">Project Overview</h2>
                <p className="text-lg">
                  This custom-designed 4-bedroom luxury villa stands as a testament to Master Home's building excellence in the heart of Kannur. Commissioned by an NRI family seeking a modern retreat that respected traditional Vastu principles, this home required meticulous planning and execution.
                </p>
                <div className="flex flex-wrap gap-4 mt-6">
                  <span className="bg-primary-50 text-primary-700 px-4 py-2 rounded-full font-semibold flex items-center gap-2"><MapPin size={18} /> Thottada, Kannur</span>
                  <span className="bg-primary-50 text-primary-700 px-4 py-2 rounded-full font-semibold flex items-center gap-2"><Building2 size={18} /> NRI Family Residence</span>
                </div>
              </section>

              {/* Challenge */}
              <section className="bg-red-50 p-8 rounded-2xl border border-red-100">
                <h3 className="text-2xl font-bold text-red-900 mb-4">The Challenge</h3>
                <ul className="space-y-4 text-red-800">
                  <li className="flex gap-3"><span className="font-bold shrink-0">1. Timeline Issues:</span> The client required completion within a strict 10-month window before an upcoming family wedding.</li>
                  <li className="flex gap-3"><span className="font-bold shrink-0">2. Land Conditions:</span> The assigned plot had a significant slope requiring deep soil retention and structurally reinforced foundation piles.</li>
                  <li className="flex gap-3"><span className="font-bold shrink-0">3. Budget Constraints:</span> While a luxury build, the client demanded strict adherence to the initial quotation with zero material waste.</li>
                </ul>
              </section>

              {/* Solution */}
              <section className="bg-green-50 p-8 rounded-2xl border border-green-100">
                <h3 className="text-2xl font-bold text-green-900 mb-4">The Solution & Execution</h3>
                <p className="text-green-800 mb-6">
                  Master Home deployed a dedicated dual-shift masonry team to accelerate the timeline without compromising structural curing periods. We utilized premium <strong className="text-green-900">UltraTech Cement</strong> for the foundation and <strong className="text-green-900">JSW Steel</strong> TMT bars to secure the sloping terrain via custom retaining walls.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-green-200">
                    <h4 className="font-bold flex items-center gap-2 mb-2"><CheckCircle2 className="text-green-600" /> Design Decisions</h4>
                    <p className="text-sm text-gray-600">Implemented cross-ventilation corridors and high ceilings to naturally cool the interiors during Kannur summers.</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-green-200">
                    <h4 className="font-bold flex items-center gap-2 mb-2"><CheckCircle2 className="text-green-600" /> Material Sourcing</h4>
                    <p className="text-sm text-gray-600">Sourced premium Kajaria vitrified tile and weather-shield external emulsion to combat heavy monsoon moisture.</p>
                  </div>
                </div>
              </section>

            </div>

            {/* Sidebar Details */}
            <div className="space-y-8">
              <div className="glass-card p-6 bg-gray-50 rounded-2xl border border-gray-100">
                <h3 className="text-xl font-bold mb-6">Project Details</h3>
                <div className="space-y-4">
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Square Footage</span>
                    <span className="font-bold text-gray-900">3,200 sq.ft</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Duration</span>
                    <span className="font-bold text-gray-900">10 Months</span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 pb-2">
                    <span className="text-gray-500">Bedrooms</span>
                    <span className="font-bold text-gray-900">4 BHK</span>
                  </div>
                  <div className="flex justify-between pb-2">
                    <span className="text-gray-500">Cost Range</span>
                    <span className="font-bold text-gray-900">₹1.2 - 1.5 Cr</span>
                  </div>
                </div>
              </div>

              {/* Client Testimonial */}
              <div className="bg-primary-900 text-white p-8 rounded-2xl shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 text-9xl text-white/10 font-serif leading-none transform translate-x-4 -translate-y-4">"</div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-yellow-400 font-bold text-xl">★★★★★</span>
                </div>
                <p className="relative z-10 text-lg leading-relaxed mb-6 font-medium italic">
                  "Master Home executed our luxury villa construction in Kannur perfectly. Despite our tight timeline, they handed over the keys right on schedule. The structural quality and finishing are world-class."
                </p>
                <div className="flex items-center gap-4 border-t border-white/20 pt-4">
                  <div className="bg-primary-600 w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl">R</div>
                  <div>
                    <h4 className="font-bold">Rajesh N.</h4>
                    <p className="text-primary-200 text-sm">NRI Homeowner, Kannur</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12 bg-gray-100 p-8 rounded-2xl">
            <h2 className="text-2xl font-bold mb-4">Want to build something similar in Kannur?</h2>
            <a href="tel:+919567810944" className="inline-flex items-center justify-center px-8 py-4 text-white bg-primary-600 rounded-full font-bold hover:bg-primary-700 transition-colors shadow-lg">
              Get Your Free Quote Today
            </a>
          </div>
        </div>
      </PageLayout>
    </>
  );
};

export default LuxuryVillaKannur;
